import { useState } from 'react'
import { format } from 'date-fns'

export interface Series {
  label: string
  color: string
  values: Array<number | null>
}

/**
 * A small dependency-free line chart: shared x axis (snapshot times), one or more series on a
 * 0..max y axis. Hover shows the values at the nearest point.
 */
export function TrendChart({
  times,
  series,
  max,
  formatValue = (v) => v.toLocaleString(),
  height = 180,
}: {
  times: string[]
  series: Series[]
  max?: number
  formatValue?: (v: number) => string
  height?: number
}) {
  const [hover, setHover] = useState<number | null>(null)
  const W = 640
  const H = height
  const pad = { l: 44, r: 12, t: 10, b: 22 }
  const all = series.flatMap((s) => s.values.filter((v): v is number => v != null))
  const top = max ?? Math.max(1, ...all) * 1.1
  const n = times.length
  const x = (i: number) => pad.l + (n <= 1 ? (W - pad.l - pad.r) / 2 : (i / (n - 1)) * (W - pad.l - pad.r))
  const y = (v: number) => pad.t + (1 - v / top) * (H - pad.t - pad.b)

  if (n === 0) return <p className="py-8 text-center text-sm text-slate-500">No snapshots yet.</p>

  const ticks = [0, 0.5, 1].map((f) => f * top)
  return (
    <div className="space-y-2">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full"
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const r = (e.currentTarget as SVGSVGElement).getBoundingClientRect()
          const px = ((e.clientX - r.left) / r.width) * W
          let best = 0
          for (let i = 1; i < n; i++) if (Math.abs(x(i) - px) < Math.abs(x(best) - px)) best = i
          setHover(best)
        }}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} stroke="#e2e8f0" />
            <text x={pad.l - 6} y={y(t) + 4} textAnchor="end" fontSize="10" fill="#94a3b8">{formatValue(t)}</text>
          </g>
        ))}
        {[0, n - 1].filter((i, k, a) => a.indexOf(i) === k).map((i) => (
          <text key={i} x={x(i)} y={H - 6} textAnchor={i === 0 ? 'start' : 'end'} fontSize="10" fill="#94a3b8">
            {format(new Date(times[i]), 'MMM d HH:mm')}
          </text>
        ))}
        {series.map((s) => {
          const pts = s.values.map((v, i) => (v == null ? null : [x(i), y(v)] as const)).filter(Boolean) as Array<readonly [number, number]>
          return (
            <g key={s.label}>
              {pts.length > 1 && <polyline fill="none" stroke={s.color} strokeWidth="2" points={pts.map((p) => p.join(',')).join(' ')} />}
              {pts.map(([px, py], i) => <circle key={i} cx={px} cy={py} r={pts.length > 30 ? 1.5 : 3} fill={s.color} />)}
            </g>
          )
        })}
        {hover != null && <line x1={x(hover)} x2={x(hover)} y1={pad.t} y2={H - pad.b} stroke="#94a3b8" strokeDasharray="3 3" />}
      </svg>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
        {series.map((s) => {
          const v = hover != null ? s.values[hover] : s.values[n - 1]
          return (
            <span key={s.label} className="inline-flex items-center gap-1.5 text-slate-600">
              <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
              {s.label}: <span className="tabular-nums font-medium">{v == null ? '—' : formatValue(v)}</span>
            </span>
          )
        })}
        {hover != null && <span className="text-slate-400">at {format(new Date(times[hover]), 'MMM d HH:mm')}</span>}
      </div>
    </div>
  )
}
